/**
 * Tracking parameters for Google Analytics
 */
export interface TrackingParams {
  org_id?: string;
  org_name?: string;
  user_id?: string;
  user_name?: string;
}

/**
 * Get tracking parameters from the current URL
 * @returns Object containing org_id, org_name, and user_id if present
 */
export const getTrackingParams = (): TrackingParams => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      org_id: searchParams.get('org_id') || undefined,
      org_name: searchParams.get('org_name') || undefined,
      user_id: searchParams.get('user_id') || undefined,
      user_name: searchParams.get('user_name') || undefined,
    };
  } catch (error) {
    console.error('Error getting tracking params:', error);
    return {};
  }
};

/**
 * Add tracking parameters to a URL
 * @param url - The URL to add tracking params to
 * @param params - Tracking parameters to add
 * @returns URL with tracking parameters added
 */
export const addTrackingParams = (url: string, params: TrackingParams): string => {
  try {
    const urlObj = new URL(url, window.location.origin);

    // Add tracking parameters if they exist
    if (params.org_id) {
      urlObj.searchParams.set('org_id', params.org_id);
    }
    if (params.org_name) {
      urlObj.searchParams.set('org_name', params.org_name);
    }
    if (params.user_id) {
      urlObj.searchParams.set('user_id', params.user_id);
    }
    if (params.user_name) {
      urlObj.searchParams.set('user_name', params.user_name);
    }

    return urlObj.toString();
  } catch (error) {
    console.error('Error adding tracking params:', error);
    return url;
  }
};

/**
 * Update the current URL with tracking parameters without reloading the page
 * @param params - Tracking parameters to persist
 */
export const persistTrackingParams = (params: TrackingParams): void => {
  try {
    const url = new URL(window.location.href);

    // Add or update tracking parameters
    if (params.org_id) {
      url.searchParams.set('org_id', params.org_id);
    }
    if (params.org_name) {
      url.searchParams.set('org_name', params.org_name);
    }
    if (params.user_id) {
      url.searchParams.set('user_id', params.user_id);
    }
    if (params.user_name) {
      url.searchParams.set('user_name', params.user_name);
    }

    // Update URL without reloading
    window.history.replaceState({}, '', url.toString());
  } catch (error) {
    console.error('Error persisting tracking params:', error);
  }
};

/**
 * Remove tracking parameters from the current URL
 */
export const removeTrackingParams = (): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('org_id');
    url.searchParams.delete('org_name');
    url.searchParams.delete('user_id');
    url.searchParams.delete('user_name');

    window.history.replaceState({}, '', url.toString());
  } catch (error) {
    console.error('Error removing tracking params:', error);
  }
};

/**
 * Append tracking parameters to all existing URL search params
 * @param baseUrl - The base URL (can be relative or absolute)
 * @param params - Tracking parameters to append
 * @returns URL with tracking parameters appended
 */
export const appendTrackingParams = (baseUrl: string, params: TrackingParams): string => {
  try {
    // Handle relative URLs
    const urlObj = baseUrl.startsWith('http')
      ? new URL(baseUrl)
      : new URL(baseUrl, window.location.origin);

    // Append tracking parameters (don't overwrite existing params)
    if (params.org_id && !urlObj.searchParams.has('org_id')) {
      urlObj.searchParams.append('org_id', params.org_id);
    }
    if (params.org_name && !urlObj.searchParams.has('org_name')) {
      urlObj.searchParams.append('org_name', params.org_name);
    }
    if (params.user_id && !urlObj.searchParams.has('user_id')) {
      urlObj.searchParams.append('user_id', params.user_id);
    }
    if (params.user_name && !urlObj.searchParams.has('user_name')) {
      urlObj.searchParams.append('user_name', params.user_name);
    }

    // Return relative URL if input was relative
    if (!baseUrl.startsWith('http')) {
      return urlObj.pathname + urlObj.search + urlObj.hash;
    }

    return urlObj.toString();
  } catch (error) {
    console.error('Error appending tracking params:', error);
    return baseUrl;
  }
};
